import { Injectable } from '@angular/core';
import Dexie from 'dexie';
import { from, Observable, of, Subject } from 'rxjs';
import {
  Human,
  BackendEnum,
  Box,
  Config,
  FaceResult,
  match,
} from '@vladmandic/human';
import { GetImageUrlService } from 'ZalamoLibrary/src/lib/core/image/image-loader/get-image-url.service';
import { map, switchMap, tap } from 'rxjs/operators';
import { maxSquareCalculator } from 'ZalamoLibrary/src/lib/zalamo/face-detection/calculateMaxSquare';
import { ImagesContainer } from '@ui/orders/graphql';
import { ZalamoObject } from 'Upload/upload/graphql';

const database = new Dexie('zalamo-strong-faces');
database.version(1).stores({
  facesStrong: `&photoId`,
});

const squareCalculator = (face, ratio, eyesY = 70, eyesDistance = 100) => {};
const cache = new Map<string, Observable<any>>();
const humanConfig: Partial<Config> = {
  // user configuration for human, used to fine-tune behavior
  debug: true,
  backend: 'humangl' as BackendEnum,
  modelBasePath: 'https://vladmandic.github.io/human-models/models/',
  filter: {
    enabled: false,
    equalization: true,
    flip: false,
    sharpness: 0.8,
    contrast: 0.8,
  },

  face: {
    enabled: true,
    detector: {
      rotation: true,
      maxDetected: 5,
      minConfidence: 0.4,
      return: true,
      enabled: false,
    },
    iris: { enabled: false },

    description: { enabled: true },
    emotion: { enabled: false },
    antispoof: { enabled: false },
    liveness: { enabled: false },
    mesh: { enabled: true },
    gear: { enabled: false },
    attention: { enabled: false },
  },
  body: { enabled: false },
  hand: { enabled: false },
  object: { enabled: false },
  gesture: { enabled: false },
  segmentation: { enabled: false },
};

@Injectable({
  providedIn: 'root',
})
export class FaceStrongDetectionService {
  human: Human;
  faceDetected = new Subject<{
    photoId: string;
    details: {
      ratio: number;
      faces: Omit<
        FaceResult,
        'meshRaw' | 'mesh' | 'annotations' | 'tensor' | 'embedding'
      >[];
      gravityCenter: Box;
    };
  }>();

  constructor(private downloader: GetImageUrlService) {}

  updateCache(photoId, details) {
    cache.set(photoId, of(details));
  }
  async init() {
    if (this.human) {
      return;
    }
    this.human = new Human(humanConfig);
    await this.human.load();
    await this.human.warmup();
    console.log('HUMAN INITIALIZED');
  }

  similarity(face1, face2) {
    return match.similarity(face1, face2);
  }

  async detectFaces(img: string) {
    console.log(img);

    return await this.human
      .detect(
        await createImageBitmap(await fetch(img).then((res) => res.blob())),
        humanConfig,
      )
      .then((r) => ({
        faces: r.face,
        gravityCenter: r.face
          .map((f) => f.boxRaw)
          .reduce(
            (a, b) => [
              a[0] === null ? b[0] : Math.min(a[0], b[0]),
              a[1] === null ? b[1] : Math.min(a[1], b[1]),
              a[2] === null ? b[0] + b[2] : Math.max(a[2], b[0] + b[2]),
              a[3] === null ? b[1] + b[3] : Math.max(a[3], b[1] + b[3]),
            ],
            [null, null, null, null],
          ),
      }));
  }

  detectFacesByPhotoId(
    photoId: string,
    photoGroupId: string,
    containerRatio = 1,
    contextId,
    photo: { width: number; height: number },
    context: ImagesContainer | ZalamoObject,
  ): Observable<{
    faces: {
      embedding: [];
      rotation: { angle: { roll: number; yaw: number; pitch: number } };
      boxRaw: number[];
      box: number[];
      annotations: {
        leftEyeLower0: number[][];
        leftEyeUpper0: number[][];
        rightEyeLower0: number[][];
        rightEyeUpper0: number[][];
        leftEyeLower1: number[][];
        leftEyeUpper1: number[][];
        rightEyeLower1: number[][];

        rightEyeUpper1: number[][];
        leftEyeLower2: number[][];
        leftEyeUpper2: number[][];
        rightEyeLower2: number[][];
        rightEyeUpper2: number[][];
        leftEyeLower3: number[][];
        leftEyeUpper3: number[][];
        rightEyeLower3: number[][];
        rightEyeUpper3: number[][];
        silhouette: number[][];
      };
    }[];
    gravityCenter: [number, number, number, number];
    maxSquare: { x: number; y: number; w: number; h: number };
  }> {
    if (!photoId) {
      return of({
        faces: [],
        gravityCenter: null,
        maxSquare: null,
      });
    }
    return from(database.table('facesStrong').get(photoId))
      .pipe(
        switchMap((r) => {
          if (r) {
            console.log('USE DB VERSION');
            return of(r);
          }
          if (cache.has(photoId)) {
            console.log('USE CACHE');
            return cache.get(photoId);
          }
          const result = this.downloader
            .transform(
              { photoId },
              photoGroupId,
              'photographer',
              context,
              contextId,
              200,
            )
            .pipe(
              map((img) => ({ img, ratio: photo.width / photo.height })),
              switchMap((img) =>
                this.detectFaces(String(img.img).match(/blob:(.*?) /)[0]).then(
                  (r) => ({
                    ...r,
                    ratio: img.ratio,
                  }),
                ),
              ),
              tap((x) => console.log(x)),
              tap((details) =>
                this.faceDetected.next({
                  photoId,
                  details: {
                    ...details,
                    faces: details.faces.map(
                      ({ embedding, mesh, meshRaw, rotation, ...face }) => face,
                    ),
                  },
                }),
              ),
              tap((x) => database.table('facesStrong').add({ ...x, photoId })),
            );

          cache.set(photoId, result);

          return result;
        }),
      )
      .pipe(
        map(({ faces, gravityCenter, ratio }) => ({
          faces,
          gravityCenter,
          maxSquare: maxSquareCalculator(
            faces,
            gravityCenter,
            ratio,
            containerRatio,
          ),
        })),
      );
  }
}
